import type { Nullable } from '@direct-frontend/stdlib';
import { getPickupPoint, getSelfPickupPoint } from '@/entities/pickupPoint';
import { type DeliveryChunk, type OrderPaymentMethod, type ServiceType } from '@/shared/api/order';
import { OrderDeliveryMethods, OrderPaymentMethods } from '@/shared/api/order';
import type { PickupPointPaymentMethod } from '@/shared/api/pickupPoint';
import { type ArrayIntervalFieldValue } from '@/shared/components/IntervalInput';
import { getDefaultPaymentMethod } from '@/shared/lib/getDefaultPaymentMethod';
import { getRequestContext } from '@/shared/lib/requestContext';
import { checkCourierAllPaymentMethods } from './checkCourierAllPaymentMethods';

const PaymentMethodMapping: Record<PickupPointPaymentMethod, OrderPaymentMethod> = {
    already_paid: OrderPaymentMethods.CARD_ONLINE,
    card_on_receipt: OrderPaymentMethods.CARD_ON_DELIVERY,
    cash_on_receipt: OrderPaymentMethods.CASH_ON_DELIVERY,
};

//  TODO SS-7371 Вынести логику на бек
export async function getAvailablePaymentMethods(props: {
    pickupId: Nullable<string>;
    pickUpWarehouseId: Nullable<string>;
    orderId: string;
    deliveryMethod: Nullable<string>;
    deliveryCourierInterval: ArrayIntervalFieldValue;
    pickupPointServiceType: Nullable<ServiceType>;
    storeSlug: string;
    deliveryChunks: Array<DeliveryChunk>;
}): Promise<OrderPaymentMethod[]> {
    const {
        pickupId,
        pickUpWarehouseId,
        deliveryMethod,
        storeSlug,
        orderId,
        pickupPointServiceType,
        deliveryCourierInterval,
        deliveryChunks,
    } = props;

    if (deliveryMethod === OrderDeliveryMethods.COURIER) {
        const allowAllPaymentMethods = checkCourierAllPaymentMethods(deliveryCourierInterval, deliveryChunks);

        if (allowAllPaymentMethods) {
            const {
                delivery: { disableOnlinePayment, disablePostPayment },
            } = getRequestContext();

            const onlinePaymentMethods = disableOnlinePayment ? [] : [OrderPaymentMethods.CARD_ONLINE];
            const postPaymentMethods = disablePostPayment
                ? []
                : [OrderPaymentMethods.CARD_ON_DELIVERY, OrderPaymentMethods.CASH_ON_DELIVERY];

            return Promise.resolve([...onlinePaymentMethods, ...postPaymentMethods]);
        }

        return Promise.resolve([getDefaultPaymentMethod()]);
    }

    if (pickupId === null && deliveryMethod === OrderDeliveryMethods.PICKUP_POINT) {
        return Promise.resolve([getDefaultPaymentMethod()]);
    }

    if (pickUpWarehouseId === null && deliveryMethod === OrderDeliveryMethods.SELF_PICK_UP) {
        return [OrderPaymentMethods.CASH_ON_DELIVERY, OrderPaymentMethods.CARD_ON_DELIVERY];
    }

    if (pickUpWarehouseId && deliveryMethod === OrderDeliveryMethods.SELF_PICK_UP) {
        const selfPickupPoint = await getSelfPickupPoint({
            targetId: pickUpWarehouseId,
            orderId,
            storeSlug,
        });

        if (selfPickupPoint) {
            return selfPickupPoint.paymentMethods.map((method) => PaymentMethodMapping[method]);
        }

        return [OrderPaymentMethods.CASH_ON_DELIVERY, OrderPaymentMethods.CARD_ON_DELIVERY];
    }

    if (pickupId && deliveryMethod === OrderDeliveryMethods.PICKUP_POINT) {
        const pickupPoint = await getPickupPoint({
            targetId: pickupId,
            storeSlug,
            serviceType: pickupPointServiceType,
            orderId,
        });

        if (pickupPoint) {
            return pickupPoint.paymentMethods.map((method) => PaymentMethodMapping[method]);
        }
    }

    return [getDefaultPaymentMethod()];
}
